import ImageIcon from "@mui/icons-material/Image";
import { Divider, FormHelperText } from "@mui/material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { ErrorComponent } from "core/components/ErrorComponent";
import type { TFunction } from "core/entities";
import type { EstimateRequestDamageInformationFormValues } from "features/EstimateRequest/entities";
import { useFieldArray, useForm } from "react-hook-form";
import { DamageLocationCheckboxGroup } from "./DamageLocationCheckboxGroup";
import { DamageLocationImageUploadCard } from "./DamageLocationImageUploadCard";
import { EstimateRequestImageUpload } from "./EstimateRequestImageUpload";

const MAX_OTHER_IMAGES = 20;

const defaultValues: EstimateRequestDamageInformationFormValues = {
  damageLocations: [],
  otherImages: [{ file: null, fileId: "", fileName: "" }],
};

export function usePublicEstimateRequestDamageInformationFormMethods() {
  return useForm<EstimateRequestDamageInformationFormValues>({
    defaultValues,
  });
}

type PublicEstimateRequestDamageInformationFormProps = {
  shopId: string;
  requestId: string;
  formMethods: ReturnType<
    typeof usePublicEstimateRequestDamageInformationFormMethods
  >;
  t: TFunction;
};

export function PublicEstimateRequestDamageInformationForm({
  formMethods,
  t,
  shopId,
  requestId,
}: PublicEstimateRequestDamageInformationFormProps) {
  const currentDamageLocations = formMethods.watch("damageLocations");
  const otherImagesFieldArray = useFieldArray({
    name: "otherImages",
    control: formMethods.control,
    rules: {
      validate: (images) => {
        const validImagesCount = images.filter(
          (image) => image.fileId && image.fileName
        ).length;
        if (validImagesCount > MAX_OTHER_IMAGES) {
          return t("otherImagesMaxCountValidation", {
            defaultValue: "You can upload up to {{max}} additional images.",
            max: MAX_OTHER_IMAGES,
          });
        }
        return true;
      },
    },
  });
  const {
    formState: { errors },
  } = formMethods;
  const missingShopIdMessage = t("missingShopIdMessage");
  if (!shopId) {
    return (
      <Box component="form">
        <ErrorComponent message={missingShopIdMessage} />
      </Box>
    );
  }
  return (
    <Box component="form">
      <Alert severity="info" sx={{ mb: 2 }}>
        {t("damageFormDescription")}
      </Alert>
      <Box
        sx={{
          display: "grid",
          alignItems: "start",
          gridTemplateColumns: { sm: "1fr", md: "auto 1fr" },
          gap: 2,
        }}
      >
        <DamageLocationCheckboxGroup
          formMethods={formMethods}
          t={t}
          formControlProps={{ sx: { minWidth: "fit-content" } }}
        />
        <Stack
          direction="row"
          mt={5}
          gap={2}
          flexWrap="wrap"
          alignItems="start"
        >
          {currentDamageLocations.map(({ location }, index) => (
            <DamageLocationImageUploadCard
              estimateRequestId={requestId}
              shopId={shopId}
              key={location}
              formMethods={formMethods}
              index={index}
              location={location}
            />
          ))}
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} textAlign="left">
        {t("additionalImagesDescription", {
          defaultValue: "Additional Images (optional) {{count}}/{{max}}",
          count: otherImagesFieldArray.fields.length - 1,
          max: MAX_OTHER_IMAGES,
        })}
      </Divider>
      {errors.otherImages?.root?.message && (
        <FormHelperText error>
          {errors.otherImages?.root?.message}
        </FormHelperText>
      )}
      <Stack direction="row" mt={5} gap={2} flexWrap="wrap" alignItems="start">
        {otherImagesFieldArray.fields.map((image, index) => (
          <EstimateRequestImageUpload
            key={image.id}
            required={false}
            control={formMethods.control}
            estimateRequestId={requestId}
            shopId={shopId}
            fileFieldName={`otherImages.${index}.file`}
            fileNameFieldName={`otherImages.${index}.fileName`}
            fileIdFieldName={`otherImages.${index}.fileId`}
            location="Other"
            imageNumber={index + 1}
            onSuccess={() => {
              otherImagesFieldArray.append({
                file: null,
                fileId: "",
                fileName: "",
              });
            }}
            onCancelClick={() => {
              otherImagesFieldArray.remove(index);
            }}
            imagePlaceholderSlot={
              <Tooltip title={t("imageDescriptionCloseup")} placement="top">
                <Stack
                  height={100}
                  width={175}
                  alignItems="center"
                  justifyContent="center"
                >
                  <ImageIcon sx={{ fontSize: "5rem" }} color="disabled" />
                </Stack>
              </Tooltip>
            }
          />
        ))}
      </Stack>
    </Box>
  );
}
